import { queryOptions, useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import { idAndNameSchema, requiredString } from '@/utils/zod'

import {
  curriculumAim,
  curriculumBasketType,
  curriculumStatus
} from '../constants/curriculum-schemas'

const semesterSchema = z.object({
  id: requiredString(),
  number: z.number(),
  hours: z.number().nullable(),
  is_active: z.boolean()
})

const basketCourseSchema = z.object({
  id: requiredString(),
  course: z
    .object({
      id: requiredString(),
      display_name: requiredString()
    })
    .nullable(),
  time_unit: z.enum(['week', 'semester', 'year']),
  semesters: z.array(semesterSchema)
})

const basketSchema = z.object({
  id: requiredString(),
  basket_type: curriculumBasketType,
  basket_courses: z.array(basketCourseSchema)
})

const responseSchema = z.object({
  id: requiredString(),
  created: z.string(),
  name: requiredString(),
  aim: curriculumAim,
  education_level: idAndNameSchema,
  status: curriculumStatus,
  baskets: z.array(basketSchema),
  can_be_deleted: z.boolean(),
  can_be_edited: z.boolean()
})

const parseSemester = (semester: z.infer<typeof semesterSchema>) => ({
  id: semester.id,
  number: semester.number,
  hours: semester.hours,
  isActive: semester.is_active
})

const parseBasket = (basket: z.infer<typeof basketSchema>) => ({
  id: basket.id,
  basketType: basket.basket_type,
  basketCourses: basket.basket_courses.map(basketCourse => ({
    id: basketCourse.id,
    course: basketCourse.course
      ? {
          id: basketCourse.course.id,
          displayName: basketCourse.course.display_name
        }
      : null,
    timeUnit: basketCourse.time_unit,
    semesters: basketCourse.semesters.map(parseSemester),
    total: basketCourse.semesters.reduce(
      (sum, semester) => sum + (semester.hours ?? 0),
      0
    )
  }))
})

const parseResponse = (response: z.infer<typeof responseSchema>) => ({
  id: response.id,
  name: response.name,
  aim: response.aim,
  educationLevel: response.education_level,
  status: response.status,
  baskets: response.baskets.map(parseBasket),
  created: new Date(response.created),
  canBeDeleted: response.can_be_deleted,
  canBeEdited: response.can_be_edited
})

export const getCurriculumDetailsQueryOptions = (curriculumId: string) =>
  queryOptions({
    queryKey: ['panelCurriculumsRetrieve', curriculumId],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelCurriculumsRetrieve(
        curriculumId,
        {
          fetchKeys: {
            id: true,
            name: true,
            aim: true,
            baskets: true,
            education_level: true,
            status: true,
            created: true,
            can_be_deleted: true,
            can_be_edited: true
          }
        }
      )
      return parseResponse(responseSchema.parse(response))
    }
  })

export const useCurriculumDetails = (curriculumId: string) =>
  useQuery(getCurriculumDetailsQueryOptions(curriculumId))
